// API service for backend integration
let API_BASE_URL = 'http://localhost:5000/api';
API_BASE_URL = 'https://crm-api.edvertotech.com/api';

class ApiService {
    constructor() {
        this.baseURL = API_BASE_URL;
    }

    // Helper method to get auth token
    getAuthToken() {
        return localStorage.getItem('authToken');
    }

    // Helper method to set auth token
    setAuthToken(token) {
        localStorage.setItem('authToken', token);
    }

    // Helper method to remove auth token
    removeAuthToken() {
        localStorage.removeItem('authToken');
    }

    // Generic request method
    async request(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`;

        const config = {
            headers: {
                'Content-Type': 'application/json',
                ...options.headers,
            },
            ...options,
        };

        // Add auth token if available
        const token = this.getAuthToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        try {
            const response = await fetch(url, config);
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || `HTTP error! status: ${response.status}`);
            }

            return data;
        } catch (error) {
            console.error('API request failed:', error);
            throw error;
        }
    }

    // Authentication APIs
    async login(employeeId, password) {
        try {
            const response = await this.request('/user/login', {
                method: 'POST',
                body: JSON.stringify({ employee_id: employeeId, password }),
            });

            if (response.token) {
                this.setAuthToken(response.token);
            }

            return response;
        } catch (error) {
            throw new Error(error.message || 'Login failed');
        }
    }

    async register(name, employeeId, password) {
        try {
            const response = await this.request('/user/register', {
                method: 'POST',
                body: JSON.stringify({ name, employee_id: employeeId, password }),
            });

            return response;
        } catch (error) {
            throw new Error(error.message || 'Registration failed');
        }
    }

    async getCurrentUser() {
        try {
            const response = await this.request('/user/me');
            return response;
        } catch (error) {
            throw new Error(error.message || 'Failed to get user info');
        }
    }

    // User management APIs
    async getAllUsers() {
        try {
            const response = await this.request('/user/users');
            return response;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch users');
        }
    }

    async createUser(userData) {
        try {
            const response = await this.request('/user/create-user', {
                method: 'POST',
                body: JSON.stringify(userData),
            });

            return response;
        } catch (error) {
            throw new Error(error.message || 'Failed to create user');
        }
    }

    async updateUser(userId, userData) {
        try {
            const response = await this.request(`/user/update-user/${userId}`, {
                method: 'PUT',
                body: JSON.stringify(userData),
            });

            return response;
        } catch (error) {
            throw new Error(error.message || 'Failed to update user');
        }
    }

    async deleteUser(userId) {
        try {
            const response = await this.request(`/user/delete-user/${userId}`, {
                method: 'DELETE',
            });

            return response;
        } catch (error) {
            throw new Error(error.message || 'Failed to delete user');
        }
    }

    // Lead management APIs
    async getAllLeads(filters = {}) {
        try {
            const queryParams = new URLSearchParams(filters).toString();
            const endpoint = `/lead/leads${queryParams ? `?${queryParams}` : ''}`;
            const response = await this.request(endpoint);
            return response;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch leads');
        }
    }

    async getLead(leadId) {
        try {
            const response = await this.request(`/lead/leads/${leadId}`);
            return response;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch lead');
        }
    }

    async createLead(leadData) {
        try {
            const response = await this.request('/lead/add-lead', {
                method: 'POST',
                body: JSON.stringify(leadData),
            });

            return response;
        } catch (error) {
            throw new Error(error.message || 'Failed to create lead');
        }
    }

    async updateLead(leadId, leadData) {
        try {
            const response = await this.request(`/lead/leads/${leadId}`, {
                method: 'PUT',
                body: JSON.stringify(leadData),
            });

            return response;
        } catch (error) {
            throw new Error(error.message || 'Failed to update lead');
        }
    }

    async assignLead(leadId, ownerId) {
        try {
            const response = await this.request(`/lead/leads/${leadId}/assign`, {
                method: 'POST',
                body: JSON.stringify({ owner_id: ownerId }),
            });

            return response;
        } catch (error) {
            throw new Error(error.message || 'Failed to assign lead');
        }
    }

    async updateLeadStatus(leadId, status) {
        try {
            const response = await this.request(`/lead/leads/${leadId}/status`, {
                method: 'PATCH',
                body: JSON.stringify({ status }),
            });

            return response;
        } catch (error) {
            throw new Error(error.message || 'Failed to update lead status');
        }
    }

    async addLeadComment(leadId, comment) {
        try {
            const response = await this.request(`/lead/leads/${leadId}/comments`, {
                method: 'POST',
                body: JSON.stringify({ comment }),
            });

            return response;
        } catch (error) {
            throw new Error(error.message || 'Failed to add comment');
        }
    }

    async addLeadEmail(leadId, email, isPrimary = false) {
        try {
            const response = await this.request(`/lead/leads/${leadId}/emails`, {
                method: 'POST',
                body: JSON.stringify({ email, isPrimary }),
            });

            return response;
        } catch (error) {
            throw new Error(error.message || 'Failed to add email');
        }
    }

    async addLeadPhone(leadId, phone, isPrimary = false) {
        try {
            const response = await this.request(`/lead/leads/${leadId}/phones`, {
                method: 'POST',
                body: JSON.stringify({ phone, isPrimary }),
            });

            return response;
        } catch (error) {
            throw new Error(error.message || 'Failed to add phone');
        }
    }

    // Delete single lead
    async deleteLead(leadId) {
        try {
            const response = await this.request(`/lead/leads/${leadId}`, {
                method: 'DELETE',
            });

            return response;
        } catch (error) {
            throw new Error(error.message || 'Failed to delete lead');
        }
    }

    // Bulk delete leads
    async bulkDeleteLeads(leadIds) {
        try {
            const response = await this.request('/lead/leads', {
                method: 'DELETE',
                body: JSON.stringify({ leadIds }),
            });

            return response;
        } catch (error) {
            throw new Error(error.message || 'Failed to delete leads');
        }
    }


    // Logout method
    logout() {
        this.removeAuthToken();
    }

    // Check if user is authenticated
    isAuthenticated() {
        return !!this.getAuthToken();
    }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService; 